import axios from 'axios';
import { Briefcase, ChevronDown, Target } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import aliceAvatar from '../../assets/avatars/alice.svg';
import { API_BASE_URL } from '../../config';
import { auth } from '../../firebase';
import storageManager from '../../utils/storageManager';
import ChatInput from './ChatInput';
import ChatMessage from './ChatMessage';
import JobSelectorModal from './JobSelectorModal';
import ResultsLimitModal from './ResultsLimitModal';

// Configure axios base URL
axios.defaults.baseURL = API_BASE_URL;

/**
 * Modern Chat Interface with Real Functionality
 * Combines ChatInterface.js functionality with modern UI components
 * Based on conversation-mockup.html design
 */
export function ModernChatInterface(props) {
  // Core states from ChatInterface.js
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [currentStep, setCurrentStep] = useState('/prompt_upload_jd');
  const [isWaitingForUpload, setIsWaitingForUpload] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescriptionText, setJobDescriptionText] = useState(null);
  const [evaluatedResumes, setEvaluatedResumes] = useState([]);

  // UI states
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);

  // Refs
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const lastSpokenMessageRef = useRef(null);

  // Job description states
  const [availableJobDescriptions, setAvailableJobDescriptions] = useState([]);
  const [showJobSelector, setShowJobSelector] = useState(true);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [showJobSelectorSection, setShowJobSelectorSection] = useState(false); // Hide fixed panel by default
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [filteredJobDescriptions, setFilteredJobDescriptions] = useState([]);
  const [queryResultsLimit, setQueryResultsLimit] = useState(50);

  // Modal states
  const [showJobSelectorModal, setShowJobSelectorModal] = useState(false);
  const [showResultsLimitModal, setShowResultsLimitModal] = useState(false);

  // Workflow metrics state for tracking communications and calls
  const [workflowMetrics, setWorkflowMetrics] = useState({
    emailsSent: 0,
    emailsFailed: 0,
    smsSent: 0,
    smsFailed: 0,
    whatsappSent: 0,
    whatsappFailed: 0,
    callsInitiated: 0,
    callsCompleted: 0,
  });

  // Helper error handler
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    setMessages(prev => [
      ...prev,
      { text: `Error: ${error.response?.data?.message || error.message}`, sender: 'system' }
    ]);
  }, []);

  // Step progression handler
  const handleStepProgression = useCallback(async (newStep) => {
    if (newStep && newStep !== currentStep) {
      try {
        let response;
        if (newStep === '/prompt_query_resumes') {
          response = await axios.get(`${API_BASE_URL}/prompt_query_resumes`);
        } else if (newStep === '/prompt_upload_jd' && messages.some(m => m.text.includes('matching candidates'))) {
          setCurrentStep(newStep);
          return;
        } else {
          response = await axios.get(`${API_BASE_URL}${newStep}`);
        }
        const data = response.data;

        setMessages(prevMessages => {
          const newMessage = { text: data.message, sender: 'system' };
          if (!prevMessages.some(msg => msg.text === newMessage.text)) {
            return [...prevMessages, newMessage];
          }
          return prevMessages;
        });

        setCurrentStep(data.next_step || newStep);
      } catch (error) {
        console.error('Error progressing to next step:', error);
        setMessages(prevMessages => [...prevMessages, { text: `Error progressing to next step: ${error.message}`, sender: 'system' }]);
      }
    }
  }, [currentStep, messages]);

  // Initial greeting effect - just like ChatInterface.js
  useEffect(() => {
    setMessages([
      { text: "Hello! I'm ALICE, your AI recruiter. How can I help you today?", sender: 'ai' },
      { text: "Please upload a job description or select from your previous searches.", sender: 'system' }
    ]);
  }, []);

  // Load available job descriptions
  useEffect(() => {
    const loadJobDescriptions = async () => {
      try {
        setIsLoadingJobs(true);
        const user = auth.currentUser;
        if (!user) {
          console.error('User not authenticated');
          return;
        }
        const idToken = await user.getIdToken();
        const response = await axios.get('/api/job_descriptions', {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        if (response.data.success) {
          setAvailableJobDescriptions(response.data.job_descriptions);
        }
      } catch (error) {
        console.error('Error loading job descriptions:', error);
      } finally {
        setIsLoadingJobs(false);
      }
    };

    loadJobDescriptions();
  }, []);

  // Filter job descriptions
  useEffect(() => {
    if (jobSearchTerm.trim() === '') {
      setFilteredJobDescriptions(availableJobDescriptions);
    } else {
      const filtered = availableJobDescriptions.filter(job =>
        job.title?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
        job.company?.toLowerCase().includes(jobSearchTerm.toLowerCase())
      );
      setFilteredJobDescriptions(filtered);
    }
  }, [jobSearchTerm, availableJobDescriptions]);

  // Handle job selection (from modal)
  const handleJobSelection = async (job) => {
    try {
      setIsWaitingForUpload(true);
      const response = await axios.post(`/api/job_descriptions/${job.id}/select`);

      if (response.data.success) {
        setJobId(response.data.job_id);
        setJobTitle(response.data.job_title);
        setJobDescriptionText(response.data.description);

        localStorage.setItem('currentJobId', response.data.job_id);
        localStorage.setItem('currentJobTitle', response.data.job_title);
        localStorage.setItem(`jobDescription_${response.data.job_id}`, response.data.description);

        setMessages(prev => [...prev, { text: response.data.message, sender: 'system' }]);

        if (response.data.next_step) {
          await handleStepProgression(response.data.next_step);
        }

        setShowJobSelector(false);
        setShowJobSelectorModal(false);
      }
    } catch (error) {
      console.error('Error selecting job description:', error);
      setMessages(prev => [...prev, {
        text: 'Failed to select job description. Please try again.',
        sender: 'system'
      }]);
    } finally {
      setIsWaitingForUpload(false);
    }
  };

  // Handle results limit update
  const handleResultsLimitUpdate = (newLimit) => {
    setQueryResultsLimit(newLimit);
    setMessages(prev => [
      ...prev,
      { text: `Results limit updated to ${newLimit} candidates.`, sender: 'system' }
    ]);
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    storageManager.clearAllData();
    setIsWaitingForUpload(true);
    const fileName = file.name;
    props.onSearchStart && props.onSearchStart(fileName);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const user = auth.currentUser;
      if (!user) {
        handleError({ message: 'User not authenticated. Please log in again.' }, 'uploading file');
        setIsWaitingForUpload(false);
        return;
      }
      const idToken = await user.getIdToken();
      const response = await axios.post('/upload_job_description', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${idToken}` }
      });

      if (response.data && response.data.job_id) {
        const newJobId = response.data.job_id;
        setJobId(newJobId);
        localStorage.setItem('currentJobId', newJobId);
        const jTitle = fileName.split('.').slice(0, -1).join('.');
        setJobTitle(jTitle);
        localStorage.setItem('currentJobTitle', jTitle);

        if (response.data.description || response.data.text || response.data.extracted_text) {
          const description = response.data.extracted_text || response.data.text || response.data.description;
          setJobDescriptionText(description);
          localStorage.setItem(`jobDescription_${newJobId}`, description);
        }
      }

      setMessages(prev => [...prev, { text: response.data.message, sender: 'system' }]);

      if (response.data.next_step) {
        await handleStepProgression(response.data.next_step);
      }
    } catch (error) {
      handleError(error, 'uploading file');
    } finally {
      setIsWaitingForUpload(false);
    }
  };

  // Handle query resumes
  const handleQueryResumes = async (input) => {
    try {
      const effectiveJobId = jobId || localStorage.getItem('currentJobId');
      if (!effectiveJobId) {
        throw new Error('No job ID available. Please upload a job description first.');
      }

      // Notify parent to show searching state
      const effectiveJobTitle = jobTitle || localStorage.getItem('currentJobTitle');
      props.onSearchStart && props.onSearchStart(input);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated. Please log in again.');
      }
      const idToken = await user.getIdToken();

      const payload = {
        query: input,
        job_id: effectiveJobId,
        limit: queryResultsLimit
      };

      const response = await axios.post('/query_resumes', payload, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.data.results) {
        const processedResults = response.data.results;
        await storageManager.storeQueryResults(effectiveJobId, processedResults);

        props.onQuerySubmit && props.onQuerySubmit({
          results: processedResults,
          jobId: effectiveJobId,
          jobTitle: effectiveJobTitle
        });

        setMessages(prev => [
          ...prev,
          {
            text: `ALICE found ${processedResults.length} matching candidates (searched up to ${queryResultsLimit} results).`,
            sender: 'system'
          },
          {
            text: 'Would you like to evaluate these candidates? (Yes/No)',
            sender: 'system'
          }
        ]);

        setCurrentStep('/handle_evaluation_response');
      } else {
        throw new Error('No results returned from query');
      }
    } catch (error) {
      console.error('Error querying resumes:', error);
      handleError(error, 'querying resumes');
      setMessages(prev => [
        ...prev,
        {
          text: `Failed to query resumes: ${error.response?.data?.error || error.message}. Would you like to try again? (Yes/No)`,
          sender: 'system'
        }
      ]);
      setCurrentStep('/prompt_query_resumes');
    }
  };

  // TTS functionality from VoiceAssistedChatSection.js
  const speakMessage = async (text) => {
    try {
      setIsSpeaking(true);
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated. Please log in again.');
      }
      const idToken = await user.getIdToken();

      const response = await axios.post('/api/tts', { text }, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      // Create audio element if it doesn't exist
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.onended = () => setIsSpeaking(false);
      }

      // Convert base64 to blob
      const audioBlob = await fetch(`data:audio/mp3;base64,${response.data.audio}`).then(r => r.blob());
      const audioUrl = URL.createObjectURL(audioBlob);

      audioRef.current.src = audioUrl;
      await audioRef.current.play();
    } catch (error) {
      console.warn('Server TTS unavailable; using browser speech:', error);
      try {
        const { speakWithBrowserTts } = await import('../../utils/browserSpeech');
        await speakWithBrowserTts(text);
      } catch (fallbackError) {
        console.error('All speech providers failed:', fallbackError);
      } finally {
        setIsSpeaking(false);
      }
    }
  };

  // Speak system/AI messages (prevent duplicates and overlapping)
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      const shouldSpeak = (lastMsg.sender === 'ai' || lastMsg.sender === 'system') &&
                         lastMsg.text &&
                         lastMsg.text !== lastSpokenMessageRef.current &&
                         !isSpeaking;

      if (shouldSpeak) {
        lastSpokenMessageRef.current = lastMsg.text;
        speakMessage(lastMsg.text);
      }
    }
  }, [messages, isSpeaking]);

  // Helper function to get display name
  const getDisplayName = (candidate) => {
    if (candidate.name && candidate.name !== "Unknown") {
      return candidate.name;
    }
    const fileName = candidate.id.split('/').pop();
    return fileName.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ") || "Unnamed Candidate";
  };

  // Update communication metrics
  const updateCommunicationMetrics = useCallback((results) => {
    setWorkflowMetrics((prev) => ({
      ...prev,
      emailsSent: results.email?.successful_emails || 0,
      emailsFailed: results.email?.failed_emails || 0,
      smsSent: results.sms?.successful_sms || 0,
      smsFailed: results.sms?.failed_sms || 0,
      whatsappSent: results.whatsapp?.successful_whatsapp || 0,
      whatsappFailed: results.whatsapp?.failed_whatsapp || 0,
    }));
  }, []);

  // Update call metrics
  const updateCallMetrics = useCallback((callResults) => {
    setWorkflowMetrics((prev) => ({
      ...prev,
      callsInitiated: callResults?.length || 0,
      callsCompleted: callResults?.filter((c) => c.call_status === "completed").length || 0,
    }));
  }, []);

  // Generate communication summary (for Gap #1)
  const generateCommunicationSummary = useCallback(() => {
    const { emailsSent, smsSent, whatsappSent } = workflowMetrics;
    const total = emailsSent + smsSent + whatsappSent;

    if (total === 0) return "No communications were sent.";

    const parts = ["📊 Communication Summary:", ""];
    if (emailsSent > 0) parts.push(`📧 Emailed ${emailsSent} candidate${emailsSent > 1 ? 's' : ''}`);
    if (smsSent > 0) parts.push(`💬 SMS sent to ${smsSent} candidate${smsSent > 1 ? 's' : ''}`);
    if (whatsappSent > 0) parts.push(`📱 WhatsApp messaged ${whatsappSent} candidate${whatsappSent > 1 ? 's' : ''}`);

    parts.push("", `Total: ${total} message${total > 1 ? 's' : ''} sent`);
    parts.push("", "✨ I will monitor responses and notify you if candidates show interest.");

    return parts.join("\n");
  }, [workflowMetrics]);

  // Generate full workflow summary (for Gap #2 & #3)
  const generateFullWorkflowSummary = useCallback(() => {
    const { emailsSent, smsSent, whatsappSent, callsInitiated, callsCompleted } = workflowMetrics;

    const totalComms = emailsSent + smsSent + whatsappSent + callsInitiated;

    const parts = [
      "🎉 Recruitment Workflow Complete!",
      "",
      "Here's what I accomplished:",
      "",
    ];

    if (emailsSent > 0) parts.push(`📧 Emailed ${emailsSent} candidate${emailsSent > 1 ? 's' : ''}`);
    if (smsSent > 0) parts.push(`💬 SMS sent to ${smsSent} candidate${smsSent > 1 ? 's' : ''}`);
    if (whatsappSent > 0) parts.push(`📱 WhatsApp messaged ${whatsappSent} candidate${whatsappSent > 1 ? 's' : ''}`);
    if (callsInitiated > 0) {
      parts.push(`☎️  Initiated ${callsInitiated} call${callsInitiated > 1 ? 's' : ''} (${callsCompleted} completed)`);
    }

    if (totalComms > 0) {
      parts.push("", `Total Outreach: ${totalComms} touch point${totalComms > 1 ? 's' : ''}`);
      parts.push("", "✨ I will notify you if these candidates are interested in the opportunity.");
    } else {
      parts.push("No communications were sent during this workflow.");
    }

    return parts.join("\n");
  }, [workflowMetrics]);

  // Handle evaluation response - Full implementation from ChatInterface.js
  const handleEvaluationResponse = async (response) => {
    const effectiveJobId = jobId || localStorage.getItem('currentJobId');
    const effectiveJobTitle = jobTitle || localStorage.getItem('currentJobTitle');

    console.log('DEBUG - Handle Evaluation Response:');
    console.log('1. Effective Job ID:', effectiveJobId);
    console.log('2. Effective Job Title:', effectiveJobTitle);

    try {
      // Get current user and token for authentication
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated. Please log in again.');
      }
      const idToken = await user.getIdToken();

      const evalResponse = await axios.post('/handle_evaluation_response', {
        response: response,
        job_id: effectiveJobId
      }, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });

      if (response.toLowerCase() === 'yes') {
        if (evalResponse.data.next_step === '/evaluate_resumes') {
          setMessages(prev => [
            ...prev,
            { text: 'Starting resume evaluation...', sender: 'system' }
          ]);

          let parsedResults = [];

          // PRIORITY 1: Use cached results from backend response (fastest - no delay!)
          if (evalResponse.data.cached_results && Array.isArray(evalResponse.data.cached_results) && evalResponse.data.cached_results.length > 0) {
            console.log('DEBUG - Using cached results from backend response:', evalResponse.data.cached_results);
            parsedResults = evalResponse.data.cached_results;
          } else {
            // PRIORITY 2: Use filtered results from localStorage (fast)
            console.log('DEBUG - Fetching filtered results from localStorage for jobId:', effectiveJobId);
            const filteredResults = storageManager.getFilteredResults(effectiveJobId);
            console.log('DEBUG - Filtered results from localStorage:', filteredResults);

            if (filteredResults && Array.isArray(filteredResults) && filteredResults.length > 0) {
              parsedResults = filteredResults;
            } else {
              // PRIORITY 3: Fallback to backend Firestore storage (SLOW - causes 3-5 min delay!)
              console.log('DEBUG - No cached or filtered results found, falling back to backend Firestore storage (this may be slow)');
              const storedResults = await storageManager.getQueryResults(effectiveJobId);
              console.log('DEBUG - Results from backend Firestore:', storedResults);
              if (
                storedResults &&
                storedResults.data &&
                Array.isArray(storedResults.data.results)
              ) {
                parsedResults = storedResults.data.results;
              }
            }
          }

          if (!Array.isArray(parsedResults) || parsedResults.length === 0) {
            // Clear any corrupted data and provide helpful message
            storageManager.clearJobData(effectiveJobId);

            setMessages(prev => [
              ...prev,
              {
                text: '⚠️ No query results found for this job. This may be due to data corruption or the results not being properly stored.',
                sender: 'system'
              },
              {
                text: 'To fix this:\n1. Please upload your job description again\n2. Or run the query process again to regenerate the results',
                sender: 'system'
              },
              {
                text: 'Would you like to upload a new job description? (Yes/No)',
                sender: 'system'
              }
            ]);
            setCurrentStep('/prompt_upload_jd');
            return;
          }

          // Filter out removed candidates before formatting
          const removedCandidateIds = new Set(props.removedCandidates || []);
          const filteredResults = parsedResults.filter(result => !removedCandidateIds.has(result.id));

          console.log('DEBUG - Results filtering for evaluation:');
          console.log('- Total results before filtering:', parsedResults.length);
          console.log('- Removed candidates:', Array.from(removedCandidateIds));
          console.log('- Results after filtering:', filteredResults.length);

          const formattedResults = filteredResults.map(result => ({
            id: result.id || `temp-${Math.random()}`,
            name: result.name || '',
            contactName: getDisplayName(result),
            email: result.email || '',
            phoneNumbers: Array.isArray(result.phoneNumbers) ? result.phoneNumbers : [],
            score: parseFloat(result.score || result.combined_score) || 0,
            similarity: parseFloat(result.similarity) || 0,
            keyword_matches: parseInt(result.keyword_match_count) || 0,
            fullText: result.fullText || result.full_text || '',
            excerpt: result.excerpt || result.text_excerpt || '',
            jobId: effectiveJobId
          }));

          const evaluationPayload = {
            job_id: effectiveJobId,
            job_title: jobTitle || localStorage.getItem('currentJobTitle'),
            results: formattedResults,
            max_evaluations: 20,
            removed_candidates: props.removedCandidates || []
          };

          console.log('Sending evaluation payload:', JSON.stringify(evaluationPayload, null, 2));
          console.log('DEBUG - Number of candidates being sent for evaluation:', formattedResults.length);
          console.log('DEBUG - Candidate IDs being sent:', formattedResults.map(r => r.id));

          const evaluationResponse = await axios.post('/evaluate_resumes', evaluationPayload, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          });

          if (evaluationResponse.data.evaluated_resumes &&
            Array.isArray(evaluationResponse.data.evaluated_resumes) &&
            evaluationResponse.data.evaluated_resumes.length > 0) {

            setEvaluatedResumes(evaluationResponse.data.evaluated_resumes);

            // Store evaluation results using backend storage with fallback
            await storageManager.storeEvaluationResults(effectiveJobId, evaluationResponse.data.evaluated_resumes);

            props.onEvaluationResults && props.onEvaluationResults({
              evaluatedResumes: evaluationResponse.data.evaluated_resumes,
              jobId: effectiveJobId,
              jobTitle: jobTitle || localStorage.getItem('currentJobTitle')
            });

            if (evaluationResponse.data.evaluation_stats) {
              const stats = evaluationResponse.data.evaluation_stats;
              setMessages(prev => [
                ...prev,
                {
                  text: `Evaluation complete! Processed ${stats.evaluated_count} out of ${stats.total_candidates} candidates.`,
                  sender: 'system'
                },
                {
                  text: 'Would you like to notify selected candidates via Email/SMS/WhatsApp? (Yes/No)',
                  sender: 'system'
                }
              ]);
            }
            setCurrentStep('/handle_evaluation_results');
          }
        }
      } else {
        // User said "No" to evaluation
        setMessages(prev => [
          ...prev,
          {
            text: evalResponse.data.message || 'Resume search completed. No evaluation requested.',
            sender: 'system'
          },
          {
            text: 'Would you like to upload another job description? (Yes/No)',
            sender: 'system'
          }
        ]);
        setCurrentStep(evalResponse.data.next_step || '/prompt_upload_jd');
      }
    } catch (error) {
      console.error('Evaluation error:', error);

      // Check if it's a storage corruption error
      if (error.message.includes('corrupted storage data') || error.message.includes('No results found for evaluation')) {
        // Force clear all storage data to start fresh
        storageManager.forceClearAllData();
        setMessages(prev => [
          ...prev,
          {
            text: `🔄 Storage corruption detected and cleared all data.`,
            sender: 'system'
          },
          {
            text: 'The system has cleared all corrupted data. Please start fresh by uploading your job description again.',
            sender: 'system'
          },
          {
            text: 'Would you like to upload a new job description? (Yes/No)',
            sender: 'system'
          }
        ]);
        setCurrentStep('/prompt_upload_jd');
      } else {
        handleError(error, 'handling evaluation response');
        setMessages(prev => [
          ...prev,
          {
            text: 'There was an error during evaluation. Would you like to try again? (Yes/No)',
            sender: 'system'
          }
        ]);
        setCurrentStep('/handle_evaluation_response');
      }
    }
  };

  // Handle evaluation results - Full implementation from ChatInterface.js
  const handleEvaluationResults = async (response) => {
    const effectiveJobId = jobId || localStorage.getItem('currentJobId');
    const effectiveJobTitle = jobTitle || localStorage.getItem('currentJobTitle');

    console.log('DEBUG - Handle Evaluation Results:');
    console.log('1. Effective Job ID:', effectiveJobId);
    console.log('2. Effective Job Title:', effectiveJobTitle);

    if (response.toLowerCase() === 'yes') {
      try {
        setIsProcessing(true);

        // Get job description from state or localStorage
        const effectiveJobDescription =
          jobDescriptionText ||
          localStorage.getItem(`jobDescription_${effectiveJobId}`);

        // Debug logging
        console.log('Using job description from:');
        console.log('- From state:', jobDescriptionText?.substring(0, 100));
        console.log('- From localStorage:',
          localStorage.getItem(`jobDescription_${effectiveJobId}`)?.substring(0, 100));

        if (!effectiveJobDescription) {
          throw new Error('Job description is required but not available');
        }

        // Validate job description format
        if (effectiveJobDescription.startsWith('%PDF')) {
          throw new Error('Invalid job description format - received PDF data');
        }

        // Format candidate results with proper data
        const formattedResults = evaluatedResumes.map(candidate => ({
          id: candidate.id,
          contactName: candidate.contactName || candidate.name || 'Unknown',  // Add fallback
          email: candidate.email,
          phoneNumbers: candidate.phoneNumbers || [],
          score: candidate.score,
          title: effectiveJobTitle,  // Add job title here
          rating: {
            key_matches: candidate.rating?.key_matches || [],
            explanation: candidate.rating?.explanation || '',
            missing_skills: candidate.rating?.missing_skills || [],
            strengths: candidate.rating?.strengths || [],
            weaknesses: candidate.rating?.weaknesses || []
          }
        }));

        const payload = {
          evaluation_results: formattedResults,
          job_description: effectiveJobDescription,
          job_title: effectiveJobTitle,  // Use effective job title
          job_id: effectiveJobId,
          min_score: 60,
          communication_methods: ['email', 'sms', 'whatsapp']
        };

        console.log('Sending evaluation payload:', payload);

        // Get authentication token
        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated');
        }
        const idToken = await user.getIdToken();

        // Make API call with authentication
        const notifyResponse = await axios.post('/handle_evaluation_results', payload, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });

        // Handle ASYNC response (202 Accepted)
        if (notifyResponse.status === 202) {
          // Processing in background
          const statusMessage = notifyResponse.data.message ||
            `Communication initiated for ${notifyResponse.data.qualified_candidates || 0} qualified candidate(s)`;

          setMessages(prev => [
            ...prev,
            {
              text: statusMessage,
              sender: 'system'
            },
            {
              text: 'Communications are being processed in the background. This may take a few moments.',
              sender: 'system'
            },
            {
              text: 'Check your backend logs for detailed status updates.',
              sender: 'system'
            }
          ]);

          console.log('Communication processing started in background');

        } else if (notifyResponse.data?.communication_results) {
          // Handle synchronous/completed communication results
          const { email, sms, whatsapp } = notifyResponse.data.communication_results;

          // Update workflow metrics
          updateCommunicationMetrics(notifyResponse.data.communication_results);

          // Track messages to add
          const statusMessages = [];

          // Handle email results
          if (email && email.status !== 'processing') {
            const emailMsg = `Email processing complete: ${email.successful_emails} sent, ` +
              `${email.failed_emails} failed, ${email.skipped || 0} skipped.`;
            statusMessages.push({
              text: emailMsg,
              sender: 'system'
            });
          }

          // Handle SMS results
          if (sms && sms.status !== 'processing') {
            const smsMsg = `SMS processing complete: ${sms.successful_sms} sent, ` +
              `${sms.failed_sms} failed, ${sms.skipped || 0} skipped.`;
            statusMessages.push({
              text: smsMsg,
              sender: 'system'
            });
          }

          // Handle WhatsApp results
          if (whatsapp && whatsapp.status !== 'processing') {
            const whatsappMsg = `WhatsApp processing complete: ${whatsapp.successful_whatsapp} sent, ` +
              `${whatsapp.failed_whatsapp} failed, ${whatsapp.skipped || 0} skipped.`;
            statusMessages.push({
              text: whatsappMsg,
              sender: 'system'
            });
          }

          // Add summary message if we have results
          if (statusMessages.length > 0) {
            statusMessages.push({
              text: `Communication complete. ${formattedResults.length} candidates processed.`,
              sender: 'system'
            });

            // ✅ GAP #1: Add communication summary
            const commSummary = generateCommunicationSummary();
            if (commSummary) {
              statusMessages.push({
                text: commSummary,
                sender: 'system'
              });
            }

            // Update messages state once with all new messages
            setMessages(prev => [...prev, ...statusMessages]);

            // Store communication results in localStorage if needed
            localStorage.setItem(
              `communication_results_${effectiveJobId}`,
              JSON.stringify(notifyResponse.data.communication_results)
            );
          }
        }

      } catch (error) {
        console.error('Evaluation results error:', error.response?.data || error);
        handleError(error, 'processing notifications');

        // Add error message to chat
        setMessages(prev => [
          ...prev,
          {
            text: `Error processing communications: ${error.message}`,
            sender: 'system'
          }
        ]);
      } finally {
        setIsProcessing(false);
      }
    }

    // Always proceed to call agent prompt
    setMessages(prev => [
      ...prev,
      {
        text: 'Would you like to activate ALICE call function for the selected candidates? (Yes/No)',
        sender: 'system'
      }
    ]);
    setCurrentStep('/prompt_activate_call_agents');
  };

  // Handle prompt call candidate - Full implementation from ChatInterface.js
  const handlePromptCallCandidate = async (input) => {
    console.log('Handling prompt call candidate with input:', input);

    // Filter candidates with score above 60
    const qualifiedCandidates = evaluatedResumes.filter(candidate =>
      (candidate.score || candidate.rating?.score || 0) >= 60
    );

    if (!qualifiedCandidates || qualifiedCandidates.length === 0) {
      setMessages(prev => [
        ...prev,
        {
          text: "No candidates with scores above 60 are available for calling.",
          sender: 'system'
        }
      ]);
      setCurrentStep('/chat');
      return;
    }

    setMessages(prev => [
      ...prev,
      {
        text: "Here are the qualified candidates (Score > 60):",
        sender: 'system'
      },
      ...qualifiedCandidates.map((candidate, index) => ({
        text: `${getDisplayName(candidate)} - Score: ${candidate.score || candidate.rating?.score}`,
        sender: 'system'
      })),
      {
        text: "Would you like to initiate calls to these candidates? (Yes/No)",
        sender: 'system'
      }
    ]);
    setCurrentStep('/activate_call_agent');
  };

  // Handle activate call agent - Full implementation from ChatInterface.js
  const handleActivateCallAgent = async (choice) => {
    console.log('handleActivateCallAgent - Starting with choice:', choice);
    try {
      const effectiveJobId = jobId || localStorage.getItem('currentJobId');
      console.log('handleActivateCallAgent - Effective Job ID:', effectiveJobId);

      if (choice.toLowerCase() === 'yes') {
        // Filter candidates above threshold
        const qualifiedCandidates = evaluatedResumes.filter(candidate =>
          (candidate.score || candidate.rating?.score || 0) >= 60
        );

        console.log('handleActivateCallAgent - Qualified candidates:', qualifiedCandidates.length);
        console.log('handleActivateCallAgent - First candidate:', qualifiedCandidates[0]);

        const payload = {
          choice: choice.toLowerCase(),
          job_id: effectiveJobId,
          candidates: qualifiedCandidates,
          min_score: 60
        };

        console.log('handleActivateCallAgent - Sending payload:', payload);

        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated. Please log in again.');
        }
        const idToken = await user.getIdToken();

        const response = await axios.post('/activate_call_agent', payload, {
          headers: { 'Authorization': `Bearer ${idToken}` }
        });
        console.log('handleActivateCallAgent - Response:', response.data);

        if (response.data.results) {
          // Update call metrics
          updateCallMetrics(response.data.results);

          props.onCallResultsUpdate && props.onCallResultsUpdate(response.data.results);
          setMessages(prev => [
            ...prev,
            {
              text: `Initiating calls to ${qualifiedCandidates.length} qualified candidates...`,
              sender: 'system'
            },
            { text: response.data.message, sender: 'system' },
            { text: 'Would you like to review the call statuses? (Yes/No)', sender: 'system' }
          ]);
          setCurrentStep('/check_call_status');
        } else {
          console.warn('handleActivateCallAgent - No results in response:', response.data);
          throw new Error('No results returned from call agent');
        }
      } else {
        console.log('handleActivateCallAgent - User declined to make calls');

        // ✅ GAP #2: Add full workflow summary when user declines calls
        const fullSummary = generateFullWorkflowSummary();
        setMessages(prev => [
          ...prev,
          { text: fullSummary, sender: 'system' },
          { text: 'Would you like to upload another job description? (Yes/No)', sender: 'system' }
        ]);
        setCurrentStep('/prompt_upload_jd');
      }
    } catch (error) {
      console.error('handleActivateCallAgent - Error:', error);
      console.error('handleActivateCallAgent - Error details:', error.response?.data);
      handleError(error, 'activating call agent');

      // Add better error messaging
      setMessages(prev => [
        ...prev,
        {
          text: `Failed to activate call agent: ${error.response?.data?.error || error.message}. Would you like to try again? (Yes/No)`,
          sender: 'system'
        }
      ]);
      setCurrentStep('/prompt_activate_call_agents');
    }
  };

  // Handle check call status - Full implementation from ChatInterface.js
  const handleCheckCallStatus = async (choice) => {
    setIsProcessing(false);

    if (choice.toLowerCase() === 'yes') {
      try {
        setMessages(prev => [
          ...prev.filter(msg => !msg.text.includes('Processing')),
          { text: 'Fetching call status...', sender: 'system' }
        ]);

        // Note: callResults would need to be passed as prop or managed in state
        if (props.callResults && Array.isArray(props.callResults) && props.callResults.length > 0) {
          const callItem = props.callResults[0];
          const callId = callItem.call_result?.call_id || callItem.call_result?.details?.id;

          if (!callId) throw new Error('No valid call ID found');

          const user = auth.currentUser;
          if (!user) {
            throw new Error('User not authenticated. Please log in again.');
          }
          const idToken = await user.getIdToken();

          const response = await axios.get(`/check_call_status/${callId}`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          });

          if (response.data) {
            setMessages(prev => [
              ...prev,
              { text: `Call status: ${response.data.status}`, sender: 'system' },
              { text: 'Would you like to view the call transcript? (Yes/No)', sender: 'system' }
            ]);
            setCurrentStep('/get_call_transcript');
          }
        } else {
          setMessages(prev => [
            ...prev,
            { text: 'No call results available at the moment.', sender: 'system' }
          ]);
        }
      } catch (error) {
        handleError(error, 'checking call status');
      }
    } else {
      setMessages(prev => [
        ...prev,
        { text: 'Okay, let me know if you need anything else.', sender: 'system' }
      ]);
      setCurrentStep('/chat');
    }
  };

  // Handle get call transcript - Full implementation from ChatInterface.js
  const handleGetCallTranscript = async (choice) => {
    if (choice.toLowerCase() === 'yes') {
      try {
        setIsLoadingTranscript(true);

        const completedCall = props.callResults?.find(result =>
          result.call_result?.call_status === 'completed' ||
          result.call_result?.details?.status === 'completed'
        );

        if (completedCall) {
          const callId = completedCall.call_result?.call_id ||
                        completedCall.call_result?.details?.id;

          if (!callId) {
            setMessages(prev => [
              ...prev,
              { text: 'Unable to retrieve call transcript. No valid call ID found.', sender: 'system' }
            ]);
            setCurrentStep('/chat');
            return;
          }

          setMessages(prev => [
            ...prev,
            { text: 'Loading transcript...', sender: 'system' }
          ]);

          const user = auth.currentUser;
          if (!user) {
            throw new Error('User not authenticated. Please log in again.');
          }
          const idToken = await user.getIdToken();

          const response = await axios.get(`/get_call_transcript/${callId}`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          });

          if (response.data) {
            setMessages(prev => [
              ...prev.filter(msg => msg.text !== 'Loading transcript...'),
              { text: 'Transcript is now available in the results section.', sender: 'system' }
            ]);

            if (props.onTranscriptReceived) {
              props.onTranscriptReceived(response.data);
            }
          }
        } else {
          // No completed calls - offer workflow summary instead
          const completeSummary = generateFullWorkflowSummary();
          setMessages(prev => [
            ...prev,
            { text: 'No completed calls available yet. Calls may still be in progress or no calls were made.', sender: 'system' },
            { text: completeSummary, sender: 'system' },
            { text: 'Would you like to upload another job description? (Yes/No)', sender: 'system' }
          ]);
          setIsLoadingTranscript(false);
          setCurrentStep('/prompt_upload_jd');
          return;
        }
      } catch (error) {
        handleError(error, 'fetching transcript');
      } finally {
        setIsLoadingTranscript(false);
        setCurrentStep('/chat');
      }
    } else {
      // ✅ GAP #3: Add complete workflow summary when user declines transcript
      const completeSummary = generateFullWorkflowSummary();
      setMessages(prev => [
        ...prev,
        { text: completeSummary, sender: 'system' },
        { text: 'Would you like to upload another job description? (Yes/No)', sender: 'system' }
      ]);
      setCurrentStep('/prompt_upload_jd');
    }
  };

  // Handle default chat
  const handleDefaultChat = async (input) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated. Please log in again.');
      }
      const idToken = await user.getIdToken();

      const response = await axios.post('/api/chat', { message: input }, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      setMessages(prev => [...prev, { text: response.data.message, sender: 'ai' }]);
    } catch (error) {
      handleError(error, 'default chat');
    }
  };

  // Handle restart workflow (when user says "Yes" to upload another job)
  const handleRestartWorkflow = () => {
    // Clear local state
    setJobId(null);
    setJobTitle('');
    setJobDescriptionText(null);
    setEvaluatedResumes([]);

    // Clear workflow metrics
    setWorkflowMetrics({
      emailsSent: 0,
      emailsFailed: 0,
      smsSent: 0,
      smsFailed: 0,
      whatsappSent: 0,
      whatsappFailed: 0,
      callsInitiated: 0,
      callsCompleted: 0,
    });

    // Reset TTS tracking
    lastSpokenMessageRef.current = null;

    // Reset messages to initial greeting
    setMessages([
      { text: "Hello! I'm ALICE, your AI recruiter. How can I help you today?", sender: 'ai' },
      { text: "Please upload a job description or select from your previous searches.", sender: 'system' }
    ]);

    // Reset step
    setCurrentStep('/prompt_upload_jd');

    // Show job selector
    setShowJobSelectorSection(true);

    // Notify parent to clear all results and state
    if (props.onWorkflowReset) {
      props.onWorkflowReset();
    }

    console.log('Workflow restarted - all state cleared including metrics');
  };

  // Main message handler
  const sendMessage = async (message, step) => {
    try {
      if (message) {
        setMessages(prev => [...prev, { text: message, sender: 'user' }]);
      }

      switch (step) {
        case '/prompt_upload_jd':
          // Handle "Yes/No" response to upload another job
          if (message && ['yes', 'no'].includes(message.toLowerCase())) {
            if (message.toLowerCase() === 'yes') {
              handleRestartWorkflow();
            } else {
              setMessages(prev => [...prev, {
                text: 'Okay, let me know if you need anything else.',
                sender: 'system'
              }]);
              setCurrentStep('/chat');
            }
          } else {
            // Handle unexpected input
            setMessages(prev => [...prev, {
              text: 'I didn\'t understand that. Would you like to upload another job description? Please answer Yes or No.',
              sender: 'system'
            }]);
          }
          break;
        case '/prompt_query_resumes':
          setMessages(prev => [
            ...prev,
            { text: 'Please enter your search query.', sender: 'system' },
            { text: `I'll search up to ${queryResultsLimit} candidates for you.`, sender: 'system' }
          ]);
          break;
        case '/query_resumes':
          await handleQueryResumes(message);
          break;
        case '/handle_evaluation_response':
          if (['yes', 'no'].includes(message.toLowerCase())) {
            await handleEvaluationResponse(message);
          } else {
            setMessages(prev => [...prev, { text: 'Please answer with Yes or No', sender: 'system' }]);
          }
          break;
        case '/handle_evaluation_results':
          if (['yes', 'no'].includes(message.toLowerCase())) {
            await handleEvaluationResults(message);
          } else {
            setMessages(prev => [...prev, { text: 'Please answer with Yes or No', sender: 'system' }]);
          }
          break;
        case '/prompt_activate_call_agents':
          await handlePromptCallCandidate(message);
          break;
        case '/activate_call_agent':
          if (['yes', 'no'].includes(message.toLowerCase())) {
            await handleActivateCallAgent(message);
          } else {
            setMessages(prev => [...prev, { text: 'Please answer with Yes or No', sender: 'system' }]);
          }
          break;
        case '/check_call_status':
          if (message && ['yes', 'no'].includes(message.toLowerCase())) {
            await handleCheckCallStatus(message);
          } else {
            setMessages(prev => [...prev, {
              text: 'Please answer Yes or No to review call statuses.',
              sender: 'system'
            }]);
          }
          break;
        case '/get_call_transcript':
          if (message && ['yes', 'no'].includes(message.toLowerCase())) {
            await handleGetCallTranscript(message);
          } else {
            setMessages(prev => [...prev, {
              text: 'Please answer Yes or No to view the call transcript.',
              sender: 'system'
            }]);
          }
          break;
        case '/chat':
          await handleDefaultChat(message);
          break;
        default:
          console.log('Unhandled step:', step);
          console.log('Available steps:', [
            '/prompt_upload_jd',
            '/query_resumes',
            '/handle_evaluation_response',
            '/handle_evaluation_results',
            '/prompt_activate_call_agents',
            '/activate_call_agent',
            '/check_call_status',
            '/get_call_transcript',
            '/chat'
          ]);
          setMessages(prev => [...prev, {
            text: 'I\'m not sure how to handle that request.',
            sender: 'system'
          }]);
          break;
      }
    } catch (error) {
      console.error('Error in sendMessage:', error);
      handleError(error, `processing step ${step}`);
    }
  };

  // Handle send message
  const handleSendMessage = (message) => {
    sendMessage(message, currentStep);
    setInput('');
  };

  // Handle voice input - Enhanced with TTS from VoiceAssistedChatSection.js
  const handleVoiceInput = () => {
    if ('webkitSpeechRecognition' in window) {
      if (!recognitionRef.current) {
        recognitionRef.current = new window.webkitSpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setInput(transcript);
          sendMessage(transcript, currentStep);
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
      }

      if (!isListening) {
        recognitionRef.current.start();
        setIsListening(true);
      } else {
        recognitionRef.current.stop();
        setIsListening(false);
      }
    } else {
      setMessages(prev => [
        ...prev,
        { text: 'Speech recognition is not supported in this browser.', sender: 'system' }
      ]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full relative">
      {/* Floating Action Buttons */}
      <div className="absolute top-4 right-6 flex gap-2 z-10">
        {/* Job Selector Button */}
        <button
          onClick={() => setShowJobSelectorModal(true)}
          className="px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition flex items-center gap-2 text-sm"
        >
          <Briefcase className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700">{jobTitle || 'Select Job'}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {/* Results Limit Button */}
        <button
          onClick={() => setShowResultsLimitModal(true)}
          className="px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition flex items-center gap-2 text-sm"
        >
          <Target className="w-4 h-4 text-gray-600" />
          <span className="text-gray-700">Limit: {queryResultsLimit}</span>
        </button>
      </div>

      {/* Chat Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <img
            src={aliceAvatar}
            alt="ALICE"
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900">ALICE AI Assistant</h3>
            <p className="text-xs text-green-500">● Online</p>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages && messages.length > 0 ? (
          messages.map((message, index) => (
            <ChatMessage
              key={index}
              message={message.text}
              sender={message.sender === 'system' || message.sender === 'ai' ? 'alice' : 'user'}
              timestamp={new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              status={message.sender === 'user' ? 'sent' : 'delivered'}
            />
          ))
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>Start a conversation with ALICE to begin your recruitment process</p>
          </div>
        )}

        {/* Processing Indicators */}
        {isProcessing && (
          <ChatMessage
            message="Processing your request..."
            sender="alice"
            timestamp=""
            status="delivered"
            isTyping={true}
          />
        )}

        {isInitiatingCall && (
          <ChatMessage
            message="Initiating call..."
            sender="alice"
            timestamp=""
            status="delivered"
            isTyping={true}
          />
        )}

        {isLoadingTranscript && (
          <ChatMessage
            message="Loading transcript..."
            sender="alice"
            timestamp=""
            status="delivered"
            isTyping={true}
          />
        )}
      </div>

      {/* Job Selector */}
      {showJobSelectorSection && (
        <div className="m-4 border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-4 bg-white border-b border-gray-200">
            <h3 className="m-0 text-gray-800 text-lg font-semibold">Previous Job Descriptions</h3>
            <div className="flex gap-2">
              <button
                className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 text-sm transition"
                onClick={() => setShowJobSelector(!showJobSelector)}
              >
                {showJobSelector ? 'Hide' : 'Show'} Previous Searches
              </button>
              <button
                className="bg-gray-200 text-gray-700 px-2 py-2 rounded hover:bg-gray-300 text-sm transition"
                onClick={() => setShowJobSelectorSection(false)}
              >
                ✕
              </button>
            </div>
          </div>
          {showJobSelector && (
            <div className="max-h-72 overflow-y-auto p-0">
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search job descriptions..."
                    value={jobSearchTerm}
                    onChange={(e) => setJobSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  />
                </div>
              </div>
              {isLoadingJobs ? (
                <div className="py-8 text-center text-gray-500 italic">Loading job descriptions...</div>
              ) : filteredJobDescriptions.length > 0 ? (
                filteredJobDescriptions.map((job) => (
                  <div
                    key={job.id}
                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-4 border-b border-gray-200 bg-white transition hover:bg-gray-50"
                  >
                    <div className="flex-1 sm:mr-4">
                      <h4 className="m-0 mb-1 text-gray-800 text-base font-semibold">{job.title}</h4>
                      <p className="m-0 mb-1 text-gray-600 text-sm">{job.company}</p>
                    </div>
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm whitespace-nowrap transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      onClick={() => handleJobSelection(job.id)}
                      disabled={isWaitingForUpload}
                    >
                      Select
                    </button>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <p className="mb-2">No previous job descriptions found.</p>
                  <p>Upload a new job description to get started.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

             {/* Input Area */}
             <ChatInput
               value={input}
               onChange={setInput}
               onSend={handleSendMessage}
               onFileUpload={handleFileUpload}
               onVoiceInput={handleVoiceInput}
               placeholder="Type your message..."
               disabled={isWaitingForUpload}
               isListening={isListening}
               isSpeaking={isSpeaking}
             />

      {/* Modals */}
      <JobSelectorModal
        isOpen={showJobSelectorModal}
        onClose={() => setShowJobSelectorModal(false)}
        jobs={availableJobDescriptions}
        onSelect={handleJobSelection}
        isLoading={isLoadingJobs}
      />

      <ResultsLimitModal
        isOpen={showResultsLimitModal}
        onClose={() => setShowResultsLimitModal(false)}
        currentLimit={queryResultsLimit}
        onUpdate={handleResultsLimitUpdate}
      />
    </div>
  );
}

export default ModernChatInterface;

